import { useState, useEffect } from "react";
import { reportApi } from "@/services/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Download, Search } from "lucide-react";
import ReportLayout from "@/components/reports/ReportLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  exportToExcel,
  formatDateForExcel,
  ExcelColumn,
} from "@/utils/exportUtils";

interface CollectionConsumer {
  billingDate: string;
  collectionEntryNumber: string;
  consumerRegistrationNumber: string;
  consumerName: string;
  serviceName: string;
  amount: number;
  amountReceived: number; // new field for total paid
}

interface FetchParams {
  page: number;
  limit: number;
  query?: string;
  fromDate?: string;
  toDate?: string;
}

const CollectionConsumerReport = () => {
  const [data, setData] = useState<CollectionConsumer[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDocs, setTotalDocs] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    query: "",
  });
  const { toast } = useToast();
  const [allTotals, setAllTotals] = useState<{
    amount: number;
    amountReceived: number;
  }>({ amount: 0, amountReceived: 0 });

  useEffect(() => {
    // Fetch all data for totals
    const fetchAllTotals = async () => {
      try {
        const params: FetchParams = {
          page: 1,
          limit: "all" as unknown as number,
        };
        if (filters.query) params.query = filters.query.trim();
        if (filters.fromDate) params.fromDate = filters.fromDate;
        if (filters.toDate) params.toDate = filters.toDate;
        const response = await reportApi.getCollectionConsumerReport(params);
        const docs = response.data?.docs || [];
        let totalAmount = 0;
        let totalPaid = 0;
        docs.forEach((item: CollectionConsumer) => {
          totalAmount += item.amount;
          totalPaid += item.amountReceived;
        });
        setAllTotals({ amount: totalAmount, amountReceived: totalPaid });
      } catch {
        setAllTotals({ amount: 0, amountReceived: 0 });
      }
    };
    fetchAllTotals();
    fetchData();
    // eslint-disable-next-line
  }, [currentPage, itemsPerPage, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: FetchParams = {
        page: currentPage,
        limit: itemsPerPage,
      };
      if (filters.query) params.query = filters.query.trim();
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      const response = await reportApi.getCollectionConsumerReport(params);
      if (response && response.data && response.data.docs) {
        setData(response.data.docs);
        setTotalDocs(response.data.totalDocs);
        setHasNextPage(response.data.hasNextPage);
      }
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast({
        title: "Error",
        description: err.message ?? "Failed to fetch report data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (
    filterName: keyof typeof filters,
    value: string
  ) => {
    setFilters({
      ...filters,
      [filterName]: value,
    });
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      query: "",
    });
    setCurrentPage(1);
    fetchData();
  };

  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      toast({
        title: "Exporting",
        description: "Preparing your Excel file...",
      });
      const exportParams: FetchParams = {
        page: 1,
        limit: "all" as unknown as number,
      };
      if (filters.query) exportParams.query = filters.query.trim();
      if (filters.fromDate) exportParams.fromDate = filters.fromDate;
      if (filters.toDate) exportParams.toDate = filters.toDate;
      const response = await reportApi.getCollectionConsumerReport(
        exportParams
      );
      const dataToExport = response.data?.docs || [];
      const columns: ExcelColumn[] = [
        { header: "SL No", key: "slNo", width: 10 },
        { header: "Billing Date", key: "billingDate", width: 15 },
        {
          header: "Collection Entry No",
          key: "collectionEntryNumber",
          width: 20,
        },
        {
          header: "Consumer Reg No",
          key: "consumerRegistrationNumber",
          width: 20,
        },
        { header: "Consumer Name", key: "consumerName", width: 25 },
        { header: "Service Name", key: "serviceName", width: 20 },
        { header: "Amount", key: "amount", width: 15 },
        { header: "Total Paid", key: "amountReceived", width: 15 },
      ];
      const formattedData = dataToExport.map(
        (item: CollectionConsumer, index: number) => ({
          slNo: index + 1,
          billingDate: formatDateForExcel(item.billingDate),
          collectionEntryNumber: item.collectionEntryNumber,
          consumerRegistrationNumber: item.consumerRegistrationNumber,
          consumerName: item.consumerName,
          serviceName: item.serviceName,
          amount: item.amount,
          amountReceived: item.amountReceived,
        })
      );
      const now = new Date();
      const dateStr = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
      const filename = `collection_consumer_report_${dateStr}.xlsx`;
      await exportToExcel(formattedData, {
        filename,
        sheetName: "Collection Consumer Report",
        columns,
        styleHeader: true,
        totalRow: {
          keys: [
            { key: "amount", label: "Total Amount" },
            { key: "amountReceived", label: "Total Paid" },
          ],
          label: "Total",
          singleRow: true,
        },
      });
      toast({
        title: "Export Successful",
        description: "Your report has been exported to Excel.",
      });
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast({
        title: "Export Failed",
        description: err.message || "Failed to export data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = data.reduce((sum, item) => sum + item.amount, 0);
  const totalPaid = data.reduce((sum, item) => sum + item.amountReceived, 0);

  return (
    <ReportLayout
      title="Collection Consumer Report"
      description="View and analyze all collection consumer records"
    >
      <Card>
        <CardContent className="py-3">
          <form
            className="flex flex-col gap-2 md:flex-row md:items-end md:gap-4 w-full"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by consumer name, reg no, phone"
                value={filters.query}
                onChange={(e) => handleFilterChange("query", e.target.value)}
                className="pl-8 w-full h-10 placeholder:text-m"
              />
            </div>
            <div className="flex flex-col md:flex-col min-w-[140px]">
              <Label htmlFor="from-date" className="mb-1">
                From Date
              </Label>
              <Input
                id="from-date"
                type="date"
                value={filters.fromDate}
                onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                className="w-full md:w-[140px]"
              />
            </div>
            <div className="flex flex-col md:flex-col min-w-[140px]">
              <Label htmlFor="to-date" className="mb-1">
                To Date
              </Label>
              <Input
                id="to-date"
                type="date"
                value={filters.toDate}
                min={filters.fromDate || undefined}
                onChange={(e) => handleFilterChange("toDate", e.target.value)}
                className="w-full md:w-[140px]"
              />
            </div>
            <Button
              variant="outline"
              type="button"
              onClick={resetFilters}
              className="h-10 px-4 mt-4 md:mt-0"
            >
              Reset
            </Button>
            <Button
              type="button"
              onClick={handleExportToExcel}
              disabled={loading || data.length === 0}
              className="whitespace-nowrap h-10 px-4 mt-4 md:mt-0"
            >
              <Download className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
          </form>
        </CardContent>
      </Card>
      <Card>
        <CardHeader></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SL No</TableHead>
                <TableHead>Billing Date</TableHead>
                <TableHead>Collection Entry No</TableHead>
                <TableHead>Consumer Reg No</TableHead>
                <TableHead>Consumer Name</TableHead>
                <TableHead>Service Name</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Total Paid</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-6">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center py-6 text-muted-foreground"
                  >
                    {filters.query || filters.fromDate || filters.toDate
                      ? "No records match your search criteria"
                      : "No records found."}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, index) => (
                  <TableRow key={item.collectionEntryNumber + index}>
                    <TableCell>
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      {formatDateForExcel(item.billingDate)}
                    </TableCell>
                    <TableCell>{item.collectionEntryNumber}</TableCell>
                    <TableCell>{item.consumerRegistrationNumber}</TableCell>
                    <TableCell>{item.consumerName}</TableCell>
                    <TableCell>{item.serviceName}</TableCell>
                    <TableCell className="text-right font-medium">
                      {item.amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {item.amountReceived.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
              {/* Total Row */}
              {!loading && data.length > 0 && (
                <TableRow className="font-bold bg-muted">
                  <TableCell colSpan={6}>Total</TableCell>
                  <TableCell className="text-right font-medium">
                    {allTotals.amount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {allTotals.amountReceived.toLocaleString()}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
            <TablePagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalDocs / itemsPerPage)}
              onPageChange={setCurrentPage}
            />
          </div>
        </CardContent>
      </Card>
    </ReportLayout>
  );
};

export default CollectionConsumerReport;
